import type { UserInfo } from "@/lib/oauth/AuthController.ts"
import type { Observable } from "rxjs"

export interface RequestPacket {
  id: number
  path: string
  payload: Record<string, unknown>
  meta?: Record<string, unknown>
  mac?: string
}

export interface ResponsePacket {
  id: number
  path: string
  payload: unknown
  failure?: FailureInfo
  meta?: Record<string, unknown>
}

export interface FailureInfo {
  authenticationError?: string
  transportError?: string
  requestError?: string
  message?: string
}

export class RpcException extends Error {
  constructor(public failure: FailureInfo, public path: string) {
    super(`RPC ${path}: ` + (failure.message || failure.requestError || failure.transportError || failure.authenticationError || "RPC call failed"))
  }
}

export class RpcTimeoutException extends Error {
  constructor(message: string) {
    super(message)
  }
}

export class NotFoundException extends Error {
  constructor(message: string) {
    super(message)
  }
}

export class OrgNotFoundException extends NotFoundException {
  constructor(orgId: string) {
    super(`Organization "${orgId}" not found`)
  }
}

export class UserInOrgNotFoundException extends NotFoundException {
  constructor(readonly user: UserInfo | null, readonly orgId: string) {
    super(user ? `User ${user.full_name} <${user.email}> is not a member in organization "${orgId}"` : `Not a member in organization "${orgId}"`)
  }
}

export class ProjectNotFoundException extends NotFoundException {
  constructor(message: string, readonly orgId?: string) {
    super(message)
  }
}

export class TaskNotFoundException extends NotFoundException {
  constructor(message: string, readonly orgId?: string, readonly projectKey?: string) {
    super(message)
  }
}

/**
 * Possible transitions:
 *   - Connecting -> Connected, AuthFailed, ConnectFailed
 *   - Connected -> AuthFailed, Closed
 */
export enum ConnectionStatus {
  // Trying to connect
  Connecting = "CONNECTING",
  // Connected and authorized
  Connected = "CONNECTED",
  AuthFailed = "AUTH_FAILED",
  ConnectFailed = "CONNECT_FAILED",
  Closed = "CLOSED"
}

export type RpcResult<R, U> =
  | { type: "regular"; value: R }
  | { type: "flux"; initial: R; updates: Observable<U> }

export function getInitial<T, K>(result: RpcResult<T, K>) {
  if (result.type !== "flux") throw new Error("Expected flux result")
  return { initial: result.initial, channel: result.updates }
}

export function getUpdates<T, K>(result: RpcResult<T, K>) {
  if (result.type !== "flux") throw new Error("Expected flux result")
  return result.updates
}

export function getResult<T, K>(result: RpcResult<T, K>): T {
  if (result.type !== "regular") throw new Error("Expected regular result")
  return (result.value == "null" ? null : result.value) as T
}
